import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { COLOR_GREY_10 } from "shared/theme/colors";
import { Column } from "../../helpers";
import { StyledTableRow } from "./styled";

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: React.CSSProperties,
  opacity?: number,
  isLast?: boolean,
) => ({
  borderBottom: `${isLast ? "hidden" : `1px solid ${COLOR_GREY_10}`}`,
  ...draggableStyle,
  ...(isDragging && {
    background: "rgb(235,235,235)",
  }),
  ...(opacity !== undefined && { opacity }),
});

type DraggableRowProps<T> = {
  columns: Column<T>[];
  item: T & { opacity?: number }; // Add opacity to type
  index: number;
  disabled: boolean;
  isLast?: boolean;
  draggableHidden?: boolean;
  onRowClick?: (item: T) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export default function DraggableRow<T extends { id: string }>(
  props: DraggableRowProps<T>,
) {
  return (
    <Draggable draggableId={props.item.id} index={props.index}>
      {(provided, snapshot) => (
        <StyledTableRow
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style || {},
            props.item.opacity,
            props.isLast,
          )}
          onClick={() => props.onRowClick && props.onRowClick(props.item)}
          onMouseEnter={props.onMouseEnter}
          onMouseLeave={props.onMouseLeave}
        >
          <TableCell
            align="left"
            style={{
              width: 24,
              display: props.draggableHidden ? "none" : "table-cell",
            }}
          >
            <IconButton {...provided.dragHandleProps} disabled={props.disabled}>
              <DragIndicatorIcon />
            </IconButton>
          </TableCell>
          {props.columns.map((column: Column<T>, index: number) => (
            <TableCell
              key={index}
              align={column.align as TableCellProps["align"]}
              style={{
                width: column.width,
                height: "3rem",
              }}
            >
              <Box
                display="flex"
                justifyContent={column.align}
                alignItems="center"
              >
                {column.renderCell(props.item)}
              </Box>
            </TableCell>
          ))}
        </StyledTableRow>
      )}
    </Draggable>
  );
}
